import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, LayoutDashboard, RefreshCw, ShieldCheck, ShieldAlert,
  TrendingUp, TrendingDown, FolderKanban, ClipboardCheck, AlertTriangle,
  X, Printer, Download, Sparkles, ChevronRight, ChevronDown, Users, Clock,
  Activity, FileText, Sunrise,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, AreaChart, Area, BarChart, Bar,
} from "recharts";
import "./AdminExecutiveDashboard.css";
import "./ExecutiveIntelligenceBriefing.css";
import AiPredictivePanel from "./AiPredictivePanel";
import EmailHealthWidget from "@/components/EmailHealthWidget";
import MetricHelp from "@/components/MetricHelp";
import ExecutiveIntelligenceBriefing from "./ExecutiveIntelligenceBriefing";
import ExecutiveAttentionPanel from "@/components/ExecutiveAttentionPanel";

const FRAMEWORK_META = {
  hipaa:       { color: "#0F766E", label: "HIPAA" },
  cfr42_part2: { color: "#7C3AED", label: "42 CFR Part 2" },
  soc2:        { color: "#1E40AF", label: "SOC 2" },
  nist_csf:    { color: "#B45309", label: "NIST CSF" },
};
const FRAMEWORK_IDS = ["hipaa", "cfr42_part2", "soc2", "nist_csf"];

function barTone(score) {
  if (score >= 90) return "good";
  if (score >= 70) return "warn";
  return "bad";
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}

// ============================================================================
// Sub-components
// ============================================================================

function ExecutiveHero({ summary }) {
  if (!summary) return null;
  const risk = summary.organizational_risk_rating || "medium";
  return (
    <section className="hw-exec__hero" data-testid="exec-hero">
      <div className="hw-exec__hero-left">
        <div className="hw-exec__hero-eyebrow">Overall Compliance Score</div>
        <div className="hw-exec__hero-score">
          <span className="hw-exec__hero-score-value" data-testid="exec-overall-score">
            {(summary.overall_compliance_score ?? 0).toFixed(1)}
          </span>
          <span className="hw-exec__hero-score-pct">/ 100</span>
        </div>
        <div className={`hw-exec__risk-chip hw-exec__risk-chip--${risk}`}
             data-testid="exec-risk-rating">
          <ShieldAlert className="w-3.5 h-3.5" />
          {summary.organizational_risk_rating_label}
        </div>
        <div className="hw-exec__hero-caption">
          Aggregated across HIPAA · 42 CFR Part 2 · SOC 2 · NIST CSF.
          Executive summary for leadership review and audit readiness.
        </div>
      </div>
      <div className="hw-exec__hero-right">
        <div className="hw-exec__hero-stat">
          <div className="hw-exec__hero-stat-label">Total Controls</div>
          <div className="hw-exec__hero-stat-value" data-testid="exec-total-controls">
            {summary.total_controls}
          </div>
          <div className="hw-exec__hero-stat-sub">
            Across {summary.frameworks_tracked} frameworks
          </div>
        </div>
        <div className="hw-exec__hero-stat">
          <div className="hw-exec__hero-stat-label">In Compliance</div>
          <div className="hw-exec__hero-stat-value" data-testid="exec-controls-compliant">
            {summary.controls_in_compliance}
          </div>
          <div className="hw-exec__hero-stat-sub">
            {summary.total_controls > 0
              ? `${((summary.controls_in_compliance / summary.total_controls) * 100).toFixed(0)}% of applicable`
              : "—"}
          </div>
        </div>
        <div className="hw-exec__hero-stat">
          <div className="hw-exec__hero-stat-label">Needs Attention</div>
          <div className="hw-exec__hero-stat-value" data-testid="exec-controls-attention">
            {summary.controls_requiring_attention}
          </div>
          <div className="hw-exec__hero-stat-sub">Partial / Non-Compliant / Not Assessed</div>
        </div>
        <div className="hw-exec__hero-stat">
          <div className="hw-exec__hero-stat-label">High-Risk Findings</div>
          <div className="hw-exec__hero-stat-value" data-testid="exec-high-risk">
            {summary.high_risk_findings}
          </div>
          <div className="hw-exec__hero-stat-sub">Critical + High priority gaps</div>
        </div>
      </div>
    </section>
  );
}

function FrameworkCard({ fw, onOpen }) {
  const meta = FRAMEWORK_META[fw.framework_id] || {};
  const score = Number(fw.readiness_score || 0);
  const tone = barTone(score);
  return (
    <button type="button" className="hw-exec__fw-card"
            onClick={() => onOpen(fw.framework_id)}
            data-testid={`exec-fw-card-${fw.framework_id}`}>
      <div className="hw-exec__fw-name">
        <span style={{ color: meta.color }}>{meta.label || fw.short_name}</span>
        <span className="hw-exec__fw-name-badge">{fw.readiness_label}</span>
      </div>
      <div className="hw-exec__fw-score">
        <span className="hw-exec__fw-score-value">{score.toFixed(1)}</span>
        <span className="hw-exec__fw-score-pct">%</span>
      </div>
      <div className="hw-exec__fw-bar">
        <div className={`hw-exec__fw-bar-fill hw-exec__fw-bar-fill--${tone}`}
             style={{ width: `${Math.min(100, Math.max(0, score))}%` }} />
      </div>
      <div className="hw-exec__fw-stats">
        <div><strong>{fw.total_controls}</strong> total</div>
        <div><strong>{fw.controls_complete}</strong> complete</div>
        <div><strong>{fw.controls_missing}</strong> missing</div>
        <div><strong>{fw.high_risk_findings}</strong> hi-risk</div>
      </div>
    </button>
  );
}

function KpiCard({ label, value, hint, tone, icon: Icon, onClick, testId }) {
  return (
    <button type="button"
            className={`hw-exec__kpi ${tone ? "hw-exec__kpi--" + tone : ""}
                        ${onClick ? "" : "hw-exec__kpi--static"}`}
            onClick={onClick} disabled={!onClick}
            data-testid={testId}
            style={onClick ? {} : { cursor: "default" }}>
      <div className="hw-exec__kpi-label">
        {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
        {label}
      </div>
      <div className="hw-exec__kpi-value">{value}</div>
      {hint ? <div className="hw-exec__kpi-hint">{hint}</div> : null}
    </button>
  );
}

// ============================================================================
// Trends charts
// ============================================================================
function TrendsCharts({ trends }) {
  // Framework readiness: merge each framework's series by date bucket.
  // We key on the ISO instant so buckets sort correctly across month boundaries,
  // and expose the formatted label separately.
  const fwSeriesMerged = useMemo(() => {
    if (!trends) return [];
    const buckets = new Map();
    for (const fw of FRAMEWORK_IDS) {
      const rows = trends.framework_readiness_series?.[fw] || [];
      for (const r of rows) {
        const iso = r.at || "";
        const existing = buckets.get(iso) || { at_iso: iso, at: fmtDate(iso) };
        existing[fw] = r.score;
        buckets.set(iso, existing);
      }
    }
    return Array.from(buckets.values()).sort((a, b) =>
      (a.at_iso > b.at_iso ? 1 : a.at_iso < b.at_iso ? -1 : 0));
  }, [trends]);

  if (!trends) return null;

  const overallData = (trends.compliance_score_series || []).map((r) => ({
    at: fmtDate(r.at), score: r.overall_score,
  }));

  const riskData = (trends.risk_series || []).map((r) => ({
    at: fmtDate(r.at),
    critical: r.critical, high: r.high, medium: r.medium, low: r.low,
  }));

  return (
    <div className="hw-exec__row2">
      <div className="hw-exec__section" data-testid="exec-trend-overall">
        <div className="hw-exec__section-head">
          <h3><TrendingUp className="w-4 h-4" /> Compliance Score Trend</h3>
        </div>
        <div className="hw-exec__chart-wrap" style={{ height: 220 }}>
          {overallData.length === 0 ? (
            <div style={{ padding: "1.5rem", color: "#64748B", fontSize: "0.85rem" }}>
              No historical scans in this window.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={overallData} margin={{ top: 10, right: 20, left: -8, bottom: 10 }}>
                <defs>
                  <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366F1" stopOpacity={0.35}/>
                    <stop offset="100%" stopColor="#6366F1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="at" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="score" stroke="#4F46E5" fill="url(#scoreGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="hw-exec__section" data-testid="exec-trend-fw">
        <div className="hw-exec__section-head">
          <h3><Activity className="w-4 h-4" /> Framework Readiness Trend</h3>
        </div>
        <div className="hw-exec__chart-wrap" style={{ height: 220 }}>
          {fwSeriesMerged.length === 0 ? (
            <div style={{ padding: "1.5rem", color: "#64748B", fontSize: "0.85rem" }}>
              No historical assessments in this window.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={fwSeriesMerged} margin={{ top: 10, right: 20, left: -8, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="at" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
                {FRAMEWORK_IDS.map((fw) => (
                  <Line key={fw} type="monotone" dataKey={fw}
                        stroke={FRAMEWORK_META[fw].color}
                        name={FRAMEWORK_META[fw].label} dot={false}
                        connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="hw-exec__section" data-testid="exec-trend-risk">
        <div className="hw-exec__section-head">
          <h3><TrendingDown className="w-4 h-4" /> Open Risk Trend (by severity)</h3>
        </div>
        <div className="hw-exec__chart-wrap" style={{ height: 220 }}>
          {riskData.length === 0 ? (
            <div style={{ padding: "1.5rem", color: "#64748B", fontSize: "0.85rem" }}>
              No risk data yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={riskData} margin={{ top: 10, right: 20, left: -8, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="at" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
                <Bar dataKey="critical" stackId="a" fill="#DC2626" />
                <Bar dataKey="high" stackId="a" fill="#F59E0B" />
                <Bar dataKey="medium" stackId="a" fill="#3B82F6" />
                <Bar dataKey="low" stackId="a" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="hw-exec__section" data-testid="exec-trend-evidence">
        <div className="hw-exec__section-head">
          <h3><FolderKanban className="w-4 h-4" /> Evidence Growth</h3>
        </div>
        <div className="hw-exec__chart-wrap" style={{ height: 220 }}>
          {(trends.evidence_series || []).length === 0 ? (
            <div style={{ padding: "1.5rem", color: "#64748B", fontSize: "0.85rem" }}>
              No new evidence added in this window.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={(trends.evidence_series || []).map(r => ({
                at: fmtDate(r.at), coverage: r.controls_with_evidence, total: r.evidence_count,
              }))} margin={{ top: 10, right: 20, left: -8, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="at" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
                <Line type="monotone" dataKey="coverage" stroke="#059669" name="Controls Covered" dot={false} />
                <Line type="monotone" dataKey="total" stroke="#0284C7" name="Evidence Items" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Drill-down drawer
// ============================================================================
function DrilldownDrawer({ kind, arg, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let url = "";
        if (kind === "framework") url = `/admin/executive-compliance/frameworks/${arg}/drilldown`;
        else if (kind === "risk") url = `/admin/executive-compliance/risks/drilldown?severity=${arg}`;
        else if (kind === "evidence") url = `/admin/executive-compliance/evidence/drilldown${arg ? `?framework=${arg}` : ""}`;
        else if (kind === "remediation") url = `/admin/executive-compliance/remediation/drilldown`;
        const { data } = await api.get(url);
        if (!cancelled) setData(data);
      } catch (e) {
        toast.error(formatApiError(e, "Drill-down failed"));
        onClose();
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [kind, arg, onClose]);

  const title = {
    framework: `Framework · ${FRAMEWORK_META[arg]?.label || arg}`,
    risk: `Risk · ${arg?.toUpperCase() || "All"}`,
    evidence: `Evidence · ${FRAMEWORK_META[arg]?.label || "All frameworks"}`,
    remediation: "Remediation Queue",
  }[kind];

  return (
    <div className="hw-exec__drawer-backdrop" onClick={onClose}>
      <div className="hw-exec__drawer" onClick={(e) => e.stopPropagation()}
           data-testid="exec-drilldown-drawer">
        <div className="hw-exec__drawer-head">
          <div>
            <div className="hw-exec__drawer-crumb">Drill-down</div>
            <div className="hw-exec__drawer-title">{title}</div>
          </div>
          <button type="button" className="hw-exec__btn" onClick={onClose}
                  data-testid="exec-drilldown-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hw-exec__drawer-body">
          {loading || !data ? (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#64748B" }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : kind === "framework" ? (
            <FrameworkDrilldownBody doc={data} />
          ) : kind === "risk" ? (
            <RiskDrilldownBody doc={data} />
          ) : kind === "evidence" ? (
            <EvidenceDrilldownBody doc={data} />
          ) : kind === "remediation" ? (
            <RemediationDrilldownBody doc={data} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function FrameworkDrilldownBody({ doc }) {
  return (
    <>
      <div className="hw-exec__section">
        <div className="hw-exec__section-head">
          <h3>{doc.framework?.short_name} — {doc.framework?.name}</h3>
        </div>
        <div className="hw-exec__kpi-grid">
          <KpiCard label="Weighted Score" value={`${doc.score?.weighted_score ?? 0}%`}
                   tone={barTone(doc.score?.weighted_score || 0) === "good" ? "good" : barTone(doc.score?.weighted_score || 0) === "warn" ? "warn" : "bad"}
                   testId="exec-drill-fw-score" />
          <KpiCard label="Total Controls" value={doc.score?.total_controls || 0}
                   testId="exec-drill-fw-total" />
          <KpiCard label="Applicable" value={doc.score?.applicable_controls || 0}
                   testId="exec-drill-fw-applicable" />
          <KpiCard label="Gap Count" value={doc.score?.gap_count || 0}
                   tone={doc.score?.gap_count ? "warn" : "good"}
                   testId="exec-drill-fw-gaps" />
        </div>
      </div>
      <div className="hw-exec__section">
        <div className="hw-exec__section-head"><h3>Controls ({doc.controls.length})</h3></div>
        <ul className="hw-exec__list" data-testid="exec-drill-fw-controls">
          {doc.controls.map((c) => (
            <li key={c.control_id}>
              <code>{c.control_id}</code>
              <div>
                <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.85rem" }}>{c.title}</div>
                <div style={{ fontSize: "0.72rem", color: "#64748B" }}>
                  {c.risk_level} · {c.status} · {c.owner || "unassigned"} · {c.evidence_count} evidence
                </div>
              </div>
              <span style={{ fontSize: "0.72rem", color: "#64748B" }}>
                {c.next_review_at ? fmtDate(c.next_review_at) : "—"}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

function RiskDrilldownBody({ doc }) {
  return (
    <>
      <div className="hw-exec__section">
        <div className="hw-exec__section-head">
          <h3>Open Exceptions ({doc.exception_count})</h3>
        </div>
        {doc.exceptions.length === 0 ? (
          <div style={{ color: "#64748B", fontSize: "0.85rem" }}>No open exceptions at this severity.</div>
        ) : (
          <ul className="hw-exec__list" data-testid="exec-drill-risk-exceptions">
            {doc.exceptions.map((e) => (
              <li key={e.id}>
                <code>{e.code || e.id.slice(0, 8)}</code>
                <div>
                  <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.85rem" }}>{e.title}</div>
                  <div style={{ fontSize: "0.72rem", color: "#64748B" }}>
                    {e.severity} · {e.status} · {e.affected_module || "—"} · {e.assigned_owner_email || "unassigned"}
                  </div>
                </div>
                <span style={{ fontSize: "0.72rem", color: e.due_date && e.due_date < new Date().toISOString() ? "#991B1B" : "#64748B" }}>
                  Due {e.due_date ? fmtDate(e.due_date) : "—"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="hw-exec__section">
        <div className="hw-exec__section-head">
          <h3>Failing Controls ({doc.failing_control_count})</h3>
        </div>
        {doc.failing_controls.length === 0 ? (
          <div style={{ color: "#64748B", fontSize: "0.85rem" }}>No failing controls at this severity.</div>
        ) : (
          <ul className="hw-exec__list" data-testid="exec-drill-risk-controls">
            {doc.failing_controls.slice(0, 50).map((c) => (
              <li key={c.control_id}>
                <code>{c.control_id}</code>
                <div>
                  <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.85rem" }}>{c.title}</div>
                  <div style={{ fontSize: "0.72rem", color: "#64748B" }}>
                    {c.risk_level} · {c.status} · {c.owner || "unassigned"}
                  </div>
                </div>
                <span style={{ fontSize: "0.72rem", color: "#64748B" }}>
                  {(c.framework_mappings || []).map((m) => m.framework.toUpperCase()).join(", ")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function EvidenceDrilldownBody({ doc }) {
  return (
    <div className="hw-exec__section">
      <div className="hw-exec__section-head">
        <h3>Evidence ({doc.count})</h3>
      </div>
      {doc.evidence.length === 0 ? (
        <div style={{ color: "#64748B", fontSize: "0.85rem" }}>No evidence for this filter.</div>
      ) : (
        <ul className="hw-exec__list" data-testid="exec-drill-evidence">
          {doc.evidence.slice(0, 50).map((e) => (
            <li key={e.evidence_id}>
              <code>{e.evidence_id}</code>
              <div>
                <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.85rem" }}>{e.title}</div>
                <div style={{ fontSize: "0.72rem", color: "#64748B" }}>
                  {e.evidence_type} · {e.status} · {(e.frameworks || []).join(", ")}
                </div>
              </div>
              <span style={{ fontSize: "0.72rem", color: "#64748B" }}>
                {e.expiration_date ? `Exp ${fmtDate(e.expiration_date)}` : "—"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RemediationDrilldownBody({ doc }) {
  return (
    <>
      <div className="hw-exec__section">
        <div className="hw-exec__section-head">
          <h3>Overdue ({doc.overdue_count})</h3>
        </div>
        {doc.overdue.length === 0 ? (
          <div style={{ color: "#065F46", fontSize: "0.85rem" }}>No overdue items — nice.</div>
        ) : (
          <ul className="hw-exec__list" data-testid="exec-drill-rem-overdue">
            {doc.overdue.map((e) => (
              <li key={e.id}>
                <code>{e.code || e.id.slice(0, 8)}</code>
                <div>
                  <div style={{ fontWeight: 600, color: "#991B1B", fontSize: "0.85rem" }}>{e.title}</div>
                  <div style={{ fontSize: "0.72rem", color: "#64748B" }}>
                    {e.severity} · {e.affected_module || "—"} · {e.assigned_owner_email || "unassigned"}
                  </div>
                </div>
                <span style={{ fontSize: "0.72rem", color: "#991B1B" }}>
                  Due {fmtDate(e.due_date)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="hw-exec__section">
        <div className="hw-exec__section-head">
          <h3>Open ({doc.open_count})</h3>
        </div>
        {doc.open.length === 0 ? (
          <div style={{ color: "#64748B", fontSize: "0.85rem" }}>No open remediation items.</div>
        ) : (
          <ul className="hw-exec__list" data-testid="exec-drill-rem-open">
            {doc.open.slice(0, 50).map((e) => (
              <li key={e.id}>
                <code>{e.code || e.id.slice(0, 8)}</code>
                <div>
                  <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.85rem" }}>{e.title}</div>
                  <div style={{ fontSize: "0.72rem", color: "#64748B" }}>
                    {e.severity} · {e.status} · {e.assigned_owner_email || "unassigned"}
                  </div>
                </div>
                <span style={{ fontSize: "0.72rem", color: "#64748B" }}>
                  {e.due_date ? fmtDate(e.due_date) : "—"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

// ============================================================================
// ============================================================================
// Morning Executive Digest (Iter 76.11)
// Collapsible so it doesn't push operational KPIs below the fold.
// Full content is preserved — nothing is dropped, only visually compacted.
// ============================================================================
const DIGEST_EXPANDED_STORAGE_KEY = "hw:exec-dashboard:digest-expanded";

function MorningDigestCard() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => {
    try {
      const v = localStorage.getItem(DIGEST_EXPANDED_STORAGE_KEY);
      return v === null ? false : v === "true";
    } catch { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    api.get("/executive-ops/daily-digest/latest")
      .then(({ data }) => { if (!cancelled) setDigest(data); })
      .catch(() => { if (!cancelled) setDigest(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const persistExpanded = (next) => {
    setExpanded(next);
    try { localStorage.setItem(DIGEST_EXPANDED_STORAGE_KEY, String(next)); } catch { /* noop */ }
  };

  if (loading) return null;
  if (!digest || !digest.narrative) return null;

  // Concise summary = first sentence or first 180 chars — whichever is shorter.
  const narrative = digest.narrative || "";
  const firstSentence = narrative.split(/(?<=[.!?])\s+/)[0] || narrative;
  const summary = firstSentence.length > 180
    ? firstSentence.slice(0, 180).trimEnd() + "…"
    : firstSentence;
  const hasMore = summary !== narrative;

  return (
    <section
      className="hw-exec__digest"
      data-testid="exec-morning-digest"
      data-expanded={expanded ? "true" : "false"}
    >
      <button
        type="button"
        className="hw-exec__digest-head"
        onClick={() => persistExpanded(!expanded)}
        data-testid="exec-morning-digest-toggle"
        aria-expanded={expanded}
      >
        <span className="hw-exec__digest-eyebrow">
          <Sunrise className="w-3.5 h-3.5" />
          Morning Executive Digest
          <span className="hw-exec__digest-date">
            · {(digest.posted_at || "").slice(0, 10)}
          </span>
          {digest.copilot_used ? (
            <span className="hw-exec__digest-chip">Copilot</span>
          ) : (
            <span className="hw-exec__digest-chip hw-exec__digest-chip--muted">Fallback</span>
          )}
        </span>
        <span className="hw-exec__digest-toggle-label">
          {expanded ? "Hide" : hasMore ? "Show full brief" : "Show"}
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </span>
      </button>
      {!expanded && (
        <div className="hw-exec__digest-summary" data-testid="exec-morning-digest-summary">
          {summary}
        </div>
      )}
      {expanded && (
        <div className="hw-exec__digest-body">
          <div className="hw-exec__digest-narrative"
               data-testid="exec-morning-digest-full">
            {narrative}
          </div>
          {(digest.citations || []).length > 0 && (
            <div className="hw-exec__digest-citations"
                 data-testid="exec-morning-digest-citations">
              <strong>Sources:</strong> {digest.citations.join(" · ")}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// Executive KPI hero (Iter 76.11) — 3 headline metrics, big and above-fold.
// Data comes straight from the existing executive_summary payload; no new
// endpoints, no calculations, no logic changes.
// ============================================================================
function ExecutiveKpiHero({ summary }) {
  if (!summary) return null;
  const orgScore = summary.overall_compliance_score ?? 0;
  const risk = summary.organizational_risk_rating || "medium";
  const orgTone = orgScore >= 90 ? "good" : orgScore >= 70 ? "warn" : "bad";
  const alertsOpen = summary.open_findings ?? summary.risks_open ?? 0;
  const alertsCritical = summary.critical_findings ?? summary.risks_critical ?? 0;
  const alertsTone = alertsCritical > 0 ? "bad" : alertsOpen > 0 ? "warn" : "good";
  const compliancePct = summary.compliance_pct ??
    (summary.total_controls
      ? Math.round(100 * (summary.controls_in_compliance || 0) / summary.total_controls)
      : orgScore);
  const complianceTone = compliancePct >= 90 ? "good" : compliancePct >= 70 ? "warn" : "bad";
  return (
    <section className="hw-exec__kpi-hero" data-testid="exec-kpi-hero">
      <div className={`hw-exec__kpi-hero-card hw-exec__kpi-hero-card--${orgTone}`}
           data-testid="exec-kpi-orghealth">
        <div className="hw-exec__kpi-hero-eyebrow">
          <Activity className="w-3.5 h-3.5" /> Organization Health
          <MetricHelp topicKey="organization_health" size="sm" side="bottom" align="start" />
        </div>
        <div className="hw-exec__kpi-hero-value">
          {orgScore.toFixed(1)}<span className="hw-exec__kpi-hero-value-sub">/100</span>
        </div>
        <div className="hw-exec__kpi-hero-meta">
          Risk rating · <strong>{summary.organizational_risk_rating_label || risk}</strong>
        </div>
      </div>
      <div className={`hw-exec__kpi-hero-card hw-exec__kpi-hero-card--${alertsTone}`}
           data-testid="exec-kpi-alerts">
        <div className="hw-exec__kpi-hero-eyebrow">
          <AlertTriangle className="w-3.5 h-3.5" /> Active Alerts
          <MetricHelp topicKey="active_alerts" size="sm" side="bottom" align="start" />
        </div>
        <div className="hw-exec__kpi-hero-value">
          {alertsOpen}
        </div>
        <div className="hw-exec__kpi-hero-meta">
          {alertsCritical > 0
            ? <><strong>{alertsCritical}</strong> critical</>
            : "No critical alerts"}
        </div>
      </div>
      <div className={`hw-exec__kpi-hero-card hw-exec__kpi-hero-card--${complianceTone}`}
           data-testid="exec-kpi-compliance">
        <div className="hw-exec__kpi-hero-eyebrow">
          <ShieldCheck className="w-3.5 h-3.5" /> Compliance
          <MetricHelp topicKey="compliance_snapshot" size="sm" side="bottom" align="start" />
        </div>
        <div className="hw-exec__kpi-hero-value">
          {compliancePct}<span className="hw-exec__kpi-hero-value-sub">%</span>
        </div>
        <div className="hw-exec__kpi-hero-meta">
          Across {summary.frameworks_tracked ?? "—"} frameworks
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Main page
// ============================================================================
export default function AdminExecutiveDashboard() {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendDays, setTrendDays] = useState(90);
  const [drill, setDrill] = useState(null);  // { kind, arg }

  const loadAll = useCallback(async (days = trendDays) => {
    setLoading(true);
    try {
      const [ov, tr] = await Promise.all([
        api.get("/admin/executive-compliance/overview"),
        api.get(`/admin/executive-compliance/trends?days=${days}`),
      ]);
      setOverview(ov.data);
      setTrends(tr.data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load executive dashboard"));
    } finally { setLoading(false); }
  }, [trendDays]);

  useEffect(() => { loadAll(trendDays); }, [loadAll, trendDays]);

  const doPrint = () => {
    // Close any open drilldown so the printed page is clean.
    setDrill(null);
    // Give React a tick to unmount the backdrop before triggering print.
    setTimeout(() => window.print(), 50);
  };

  const doExportJson = () => {
    const payload = { overview, trends, exported_at: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `harborwell-executive-compliance-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Executive summary exported");
  };

  return (
    <div className="hw-exec" data-testid="admin-executive-dashboard">
      <div className="hw-exec__header">
        <div>
          <h2>
            <LayoutDashboard className="w-5 h-5" style={{ display: "inline", marginRight: "0.4rem", verticalAlign: "text-bottom" }} />
            Executive Compliance Dashboard
          </h2>
          <p className="hw-exec__subtitle">
            Monitor your organization&apos;s compliance posture across HIPAA,
            42 CFR Part 2, SOC 2, and NIST CSF in real time. Select any
            metric to view detailed information without leaving the dashboard.
          </p>
        </div>
        <div className="hw-exec__toolbar">
          <select value={trendDays} onChange={(e) => setTrendDays(Number(e.target.value))}
                  className="hw-exec__btn" data-testid="exec-trend-window"
                  style={{ paddingRight: "1.5rem" }}>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last 12 months</option>
          </select>
          <button type="button" className="hw-exec__btn" onClick={() => loadAll(trendDays)}
                  data-testid="exec-refresh" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button type="button" className="hw-exec__btn" onClick={doPrint}
                  data-testid="exec-print">
            <Printer className="w-4 h-4" /> Print
          </button>
          <button type="button" className="hw-exec__btn" onClick={doExportJson}
                  data-testid="exec-export" disabled={!overview}>
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {loading && !overview ? (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "3rem", color: "#64748B" }}>
          <Loader2 className="w-5 h-5 animate-spin" /> Loading executive dashboard…
        </div>
      ) : !overview ? null : (
        <>
          {/* Iter 76.17 — Executive Intelligence Briefing (Feature 10 hierarchy):
               Snapshot → Summary → Risks → Actions → Evidence → Timeline.
               Detailed analytics (KPI Hero, Executive Hero, Framework Readiness,
               etc.) render below the briefing so executives see decision-support
               content first. */}
          <ExecutiveIntelligenceBriefing overview={overview} />
          <ExecutiveKpiHero summary={overview.executive_summary} />
          <ExecutiveAttentionPanel />
          <ExecutiveHero summary={overview.executive_summary} />

          {/* Framework Readiness */}
          <section className="hw-exec__section" data-testid="exec-fw-section">
            <div className="hw-exec__section-head">
              <h3><ShieldCheck className="w-4 h-4" /> Framework Readiness</h3>
            </div>
            <p className="hw-exec__section-hint">
              Click any framework card to drill into its control inventory and gap analysis.
            </p>
            <div className="hw-exec__fw-grid">
              {overview.framework_readiness.map((fw) => (
                <FrameworkCard key={fw.framework_id} fw={fw}
                               onOpen={(id) => setDrill({ kind: "framework", arg: id })} />
              ))}
            </div>
          </section>

          <div className="hw-exec__row2">
            {/* Risk Dashboard */}
            <section className="hw-exec__section" data-testid="exec-risk-section">
              <div className="hw-exec__section-head">
                <h3><AlertTriangle className="w-4 h-4" /> Risk Dashboard</h3>
              </div>
              <div className="hw-exec__kpi-grid">
                <KpiCard label="Critical" value={overview.risk_dashboard.totals.critical}
                         tone="critical" icon={AlertTriangle}
                         onClick={() => setDrill({ kind: "risk", arg: "critical" })}
                         testId="exec-risk-critical" />
                <KpiCard label="High" value={overview.risk_dashboard.totals.high}
                         tone="high" icon={AlertTriangle}
                         onClick={() => setDrill({ kind: "risk", arg: "high" })}
                         testId="exec-risk-high" />
                <KpiCard label="Medium" value={overview.risk_dashboard.totals.medium}
                         tone="medium" icon={AlertTriangle}
                         onClick={() => setDrill({ kind: "risk", arg: "medium" })}
                         testId="exec-risk-medium" />
                <KpiCard label="Low" value={overview.risk_dashboard.totals.low}
                         tone="low" icon={AlertTriangle}
                         onClick={() => setDrill({ kind: "risk", arg: "low" })}
                         testId="exec-risk-low" />
                <KpiCard label="Trending (30d)"
                         value={overview.risk_dashboard.trending_risks_30d}
                         hint="Open risks created in last 30 days"
                         icon={TrendingUp}
                         testId="exec-risk-trending" />
                <KpiCard label="Newly Identified"
                         value={overview.risk_dashboard.newly_identified_30d}
                         hint="Created in last 30 days"
                         icon={Sparkles}
                         testId="exec-risk-new" />
              </div>
            </section>

            {/* Evidence Dashboard */}
            <section className="hw-exec__section" data-testid="exec-evidence-section">
              <div className="hw-exec__section-head">
                <h3><FolderKanban className="w-4 h-4" /> Evidence Dashboard</h3>
              </div>
              <div className="hw-exec__kpi-grid">
                <KpiCard label="Coverage"
                         value={`${overview.evidence_dashboard.coverage_pct}%`}
                         hint={`${overview.evidence_dashboard.controls_with_evidence}/${overview.evidence_dashboard.total_controls} controls`}
                         tone={overview.evidence_dashboard.coverage_pct >= 80 ? "good" :
                               overview.evidence_dashboard.coverage_pct >= 50 ? "warn" : "bad"}
                         icon={ShieldCheck}
                         onClick={() => setDrill({ kind: "evidence", arg: null })}
                         testId="exec-ev-coverage" />
                <KpiCard label="Total Evidence"
                         value={overview.evidence_dashboard.total_evidence}
                         icon={FolderKanban}
                         onClick={() => setDrill({ kind: "evidence", arg: null })}
                         testId="exec-ev-total" />
                <KpiCard label="Missing Evidence"
                         value={overview.evidence_dashboard.missing_evidence}
                         tone={overview.evidence_dashboard.missing_evidence === 0 ? "good" : "warn"}
                         icon={FileText}
                         testId="exec-ev-missing" />
                <KpiCard label="Expiring Soon"
                         value={overview.evidence_dashboard.expiring_evidence}
                         tone={overview.evidence_dashboard.expiring_evidence === 0 ? "good" : "warn"}
                         icon={Clock}
                         testId="exec-ev-expiring" />
                <KpiCard label="Expired"
                         value={overview.evidence_dashboard.expired_evidence}
                         tone={overview.evidence_dashboard.expired_evidence === 0 ? "good" : "bad"}
                         icon={AlertTriangle}
                         testId="exec-ev-expired" />
                <KpiCard label="Recently Updated"
                         value={(overview.evidence_dashboard.recently_updated || []).length}
                         hint="Last 5 items shown on drill-down"
                         icon={Activity}
                         onClick={() => setDrill({ kind: "evidence", arg: null })}
                         testId="exec-ev-recent" />
              </div>
              {/* By framework mini-chips */}
              <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.4rem", flexWrap: "wrap", fontSize: "0.72rem", color: "#475569" }}>
                {Object.entries(overview.evidence_dashboard.by_framework || {}).map(([fw, n]) => (
                  <button key={fw} type="button" className="hw-exec__btn"
                          style={{ padding: "0.2rem 0.55rem", fontSize: "0.72rem" }}
                          onClick={() => setDrill({ kind: "evidence", arg: fw })}
                          data-testid={`exec-ev-fw-chip-${fw}`}>
                    {FRAMEWORK_META[fw]?.label || fw}: <strong style={{ marginLeft: "0.2rem" }}>{n}</strong>
                    <ChevronRight className="w-3 h-3" />
                  </button>
                ))}
              </div>
            </section>
          </div>

          {/* Remediation Dashboard */}
          <section className="hw-exec__section" data-testid="exec-remediation-section">
            <div className="hw-exec__section-head">
              <h3><ClipboardCheck className="w-4 h-4" /> Remediation Dashboard</h3>
            </div>
            <div className="hw-exec__kpi-grid">
              <KpiCard label="Open Remediation"
                       value={overview.remediation_dashboard.open_remediation}
                       icon={ClipboardCheck}
                       onClick={() => setDrill({ kind: "remediation", arg: null })}
                       testId="exec-rem-open" />
              <KpiCard label="Overdue"
                       value={overview.remediation_dashboard.overdue_remediation}
                       tone={overview.remediation_dashboard.overdue_remediation === 0 ? "good" : "bad"}
                       icon={AlertTriangle}
                       onClick={() => setDrill({ kind: "remediation", arg: null })}
                       testId="exec-rem-overdue" />
              <KpiCard label="Upcoming Reviews (30d)"
                       value={overview.remediation_dashboard.upcoming_reviews_30d}
                       icon={Clock}
                       testId="exec-rem-reviews" />
              <KpiCard label="Assigned Owners"
                       value={overview.remediation_dashboard.assigned_owners}
                       icon={Users}
                       testId="exec-rem-owners" />
              <KpiCard label="Avg Resolution"
                       value={overview.remediation_dashboard.avg_resolution_hours != null
                              ? `${overview.remediation_dashboard.avg_resolution_hours}h` : "—"}
                       icon={Activity}
                       testId="exec-rem-avg" />
              <KpiCard label="SLA Met"
                       value={overview.remediation_dashboard.sla_met_pct != null
                              ? `${overview.remediation_dashboard.sla_met_pct}%` : "—"}
                       tone={overview.remediation_dashboard.sla_met_pct == null ? undefined :
                             overview.remediation_dashboard.sla_met_pct >= 90 ? "good" :
                             overview.remediation_dashboard.sla_met_pct >= 70 ? "warn" : "bad"}
                       icon={TrendingUp}
                       testId="exec-rem-sla" />
            </div>
          </section>

          {/* Trends */}
          <TrendsCharts trends={trends} />

          {/* Email Infrastructure Health — Iter 74.12 */}
          <section style={{ margin: "20px 0" }} data-testid="exec-email-health-slot">
            <EmailHealthWidget />
          </section>

          {/* AI Predictive Compliance — Wave C Phase 4 */}
          <AiPredictivePanel />
        </>
      )}

      {drill && (
        <DrilldownDrawer kind={drill.kind} arg={drill.arg}
                         onClose={() => setDrill(null)} />
      )}
    </div>
  );
}
